import { useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/lib/api/hooks'
import { dateTime } from '@/lib/format'
import { hu } from '@/lib/i18n/hu'
import { useNavigate } from 'react-router-dom'

export function NotificationBell() {
  const { data, isLoading } = useNotifications(false)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const nav = useNavigate()

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const unread = (data ?? []).filter((n) => !n.readAt)
  const unreadCount = unread.length

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`${hu.notifications.title} (${unreadCount})`}
        className="relative rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 rounded-lg border border-slate-200 bg-white shadow-xl z-30 dark:bg-slate-900 dark:border-slate-800">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
            <span className="text-sm font-semibold dark:text-slate-200">{hu.notifications.title}</span>
            <button
              onClick={() => markAll.mutate()}
              className="text-xs text-brand-600 hover:underline"
              disabled={unreadCount === 0}
            >
              {hu.notifications.markAllRead}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {isLoading && <div className="p-4 text-sm text-slate-500 dark:text-slate-400">{hu.common.loading}</div>}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <div className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">{hu.notifications.empty}</div>
            )}
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data?.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => {
                      if (!n.readAt) markRead.mutate(n.id)
                      if (n.subjectType === 'invoice' && n.subjectId) {
                        nav(`/invoices/${n.subjectId}`)
                      } else if (n.subjectType === 'accountant_export') {
                        nav('/monthly-close')
                      }
                      setOpen(false)
                    }}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800',
                      !n.readAt && 'bg-brand-50/30 dark:bg-brand-900/20',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1 h-2 w-2 rounded-full flex-shrink-0',
                        n.severity === 'urgent'
                          ? 'bg-red-600'
                          : n.severity === 'warning'
                            ? 'bg-amber-500'
                            : 'bg-sky-500',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm',
                          !n.readAt ? 'font-semibold' : 'font-medium',
                          'text-slate-900 dark:text-slate-100',
                        )}
                      >
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 dark:text-slate-300">{n.body}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">{dateTime(n.createdAt)}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
